import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { Eye, Save } from "lucide-react";
import { useCallback, useState } from "react";
import { StatusSelector } from "../components/StatusSelector";
import VisualizadorArquivo from "../components/visualizadorArquivo";
import { StatusAtendimento } from "../constants/statusAtendimento";
import { TriagemProps } from "../interfaces/triagem.interface";
import { TriagemFormData, getInitialData } from "../models/triagem.model";
import { buscarPdfParaVisualizacao } from "../utils/fetchPdfParaVisualizacao";
import { saveFormProtocolo } from "../utils/formSaveHelper";
import { CampoObrigatorio, validarCamposObrigatorios } from "../utils/formValidation";

export const Triagem = ({ onDataChange, initialData, atendimentoId, pacienteId, empresaId }: TriagemProps) => {
  const [formData, setFormData] = useState<TriagemFormData>(() => {
    const initialFormData = getInitialData(initialData);
    // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
    if (!initialFormData.status_atendimento) {
      return { ...initialFormData, status_atendimento: StatusAtendimento.NOVO };
    }
    return initialFormData;
  });
  const [statusAtendimento, setStatusAtendimento] = useState<string | null>(() => {
    const initialFormData = getInitialData(initialData);
    return initialFormData.status_atendimento || StatusAtendimento.NOVO;
  });
  const [protocoloId, setProtocoloId] = useState<number | undefined>(initialData?.id);
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Mutation para salvar o formulário
  const saveMutation = useMutation({
    mutationFn: async (data: TriagemFormData) => {
      return await saveFormProtocolo({
        endpoint: '/api/protocolo-triagem',
        data,
        atendimentoId: atendimentoId!,
        pacienteId: pacienteId,
        empresaId: empresaId,
        protocoloId: protocoloId || initialData?.id,
        statusAtendimento,
        formName: 'Triagem'
      });
    },
    onSuccess: (response) => {
      // Atualizar o protocoloId com o ID retornado
      if (response?.result?.id) {
        setProtocoloId(response.result.id);
        // Atualizar formData com os dados retornados
        const updatedData = getInitialData(response.result);
        setFormData(updatedData);
        // Atualizar status se presente
        if (response.result.status_atendimento) {
          setStatusAtendimento(response.result.status_atendimento);
        }
        // Atualizar o componente pai através do onDataChange
        if (onDataChange) {
          onDataChange({
            ...updatedData,
            id: response.result.id,
            id_paciente: response.result.id_paciente,
            id_atendimento: response.result.id_atendimento,
            id_empresa: response.result.id_empresa
          } as any);
        }
      } else if (statusAtendimento) {
        // Fallback: apenas atualizar status se não houver resposta completa
        setFormData((prevData) => ({
          ...prevData,
          status_atendimento: statusAtendimento
        }));
      }
      
      // Disparar evento para atualizar painel de exames e documentos
      if (atendimentoId) {
        const event = new CustomEvent('examesAtualizados', {
          detail: { atendimentoId }
        });
        window.dispatchEvent(event);
      }
      
      toast.success("Formulário salvo com sucesso!");
    },
    onError: (error: any) => {
      console.error('Erro ao salvar triagem:', error);
      toast.error("Não foi possível salvar o formulário.");
    }
  });

  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData((prevData) => {
      const newData = { ...prevData, [field]: value };
      
      // Calcular IMC automaticamente quando peso e altura são preenchidos
      if (field === 'peso' || field === 'altura') {
        const peso = field === 'peso' ? parseFloat(value) : parseFloat(prevData.peso);
        const altura = field === 'altura' ? parseFloat(value) : parseFloat(prevData.altura);
        
        if (peso && altura && altura > 0) {
          newData.imc = (peso / (altura * altura)).toFixed(1);
        }
      }
      
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento }
        : newData;
      
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSave = useCallback(async () => {
    if (!atendimentoId) {
      toast.error("ID do atendimento não fornecido.");
      return;
    }

    // Definir campos obrigatórios para Triagem
    // Nota: Segundo o schema do backend, todos os campos são opcionais
    const camposObrigatorios: CampoObrigatorio[] = [];

    const dataToSave = statusAtendimento 
      ? { ...formData, status_atendimento: statusAtendimento } as any
      : formData;

    // Validar campos obrigatórios
    if (!validarCamposObrigatorios(dataToSave, camposObrigatorios)) {
      return;
    }

    try {
      console.log('Salvando triagem:', { endpoint: '/api/protocolo-triagem', data: dataToSave, atendimentoId });
      await saveMutation.mutateAsync(dataToSave);
      console.log('Triagem salva com sucesso');
    } catch (error) {
      console.error('Erro ao salvar triagem:', error);
      toast.error("Não foi possível salvar o formulário. Verifique o console para mais detalhes.");
    }
  }, [atendimentoId, formData, statusAtendimento, saveMutation]);

  // Função para verificar se há dados preenchidos no formulário
  const hasFormData = useCallback(() => {
    const fieldsToCheck = [
      'motivoConsulta',
      'historicoMedico',
      'medicamentos',
      'alergias',
      'cirurgiasAnteriores',
      'pressaoArterial',
      'frequenciaCardiaca',
      'temperatura',
      'peso',
      'altura',
      'imc',
      'estadoGeral',
      'observacoes',
      'fumaCigarros',
      'usaAlcool',
      'praticaExercicios'
    ];

    return fieldsToCheck.some(field => {
      const value = formData[field as keyof TriagemFormData];
      return value && String(value).trim().length > 0;
    });
  }, [formData]);

  // Função para visualizar o PDF
  const handleVisualizar = useCallback(async () => {
    const currentProtocoloId = protocoloId || initialData?.id;
    
    if (!currentProtocoloId) {
      toast.warning("Salve o formulário antes de visualizar.");
      return;
    }

    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const url = `${BASE_URL}/api/protocolo-triagem/${currentProtocoloId}/imprimir`;
      const blob = await buscarPdfParaVisualizacao({
        atendimentoId: atendimentoId,
        protocoloId: currentProtocoloId,
        tipoProtocolo: "triagem",
        fallbackUrl: url
      });

      const urlBlob = window.URL.createObjectURL(blob);
      setPdfUrl(urlBlob);
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(error?.message || "Não foi possível gerar o PDF.");
    }
  }, [protocoloId, initialData?.id, atendimentoId]);

  const fecharVisualizador = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setVisualizadorAberto(false);
    setPdfUrl(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending 
            ? (protocoloId || initialData?.id ? 'Atualizando...' : 'Salvando...') 
            : (protocoloId || initialData?.id ? 'Atualizar' : 'Salvar')}
        </Button>
        <Button 
          variant="outline" 
          onClick={handleVisualizar}
          disabled={!protocoloId && !initialData?.id}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar Documento
        </Button>
      </div>
      <StatusSelector 
        atendimentoId={atendimentoId} 
        formName="Triagem"
        value={statusAtendimento || formData.status_atendimento}
        onStatusSelect={(status) => {
          setStatusAtendimento(status || null);
          // Atualizar também no formData para manter sincronizado
          if (status) {
            setFormData((prevData) => ({
              ...prevData,
              status_atendimento: status
            }));
          }
        }}
      />
      <div>
        <Label>Motivo da Consulta</Label>
        <Textarea 
          placeholder="Descreva o motivo da consulta..." 
          rows={2} 
          value={formData.motivoConsulta}
          onChange={(e) => handleInputChange('motivoConsulta', e.target.value)}
        />
      </div>

      <div>
        <Label>Histórico Médico</Label>
        <Textarea 
          placeholder="Descreva o histórico médico do paciente..." 
          rows={3} 
          value={formData.historicoMedico}
          onChange={(e) => handleInputChange('historicoMedico', e.target.value)}
        />
      </div>

      <div>
        <Label>Medicamentos em Uso</Label>
        <Textarea 
          placeholder="Liste os medicamentos que o paciente está utilizando..." 
          rows={2} 
          value={formData.medicamentos}
          onChange={(e) => handleInputChange('medicamentos', e.target.value)}
        />
      </div>

      <div>
        <Label>Alergias</Label>
        <Input 
          placeholder="Descreva alergias conhecidas..." 
          value={formData.alergias}
          onChange={(e) => handleInputChange('alergias', e.target.value)}
        />
      </div>

      <div>
        <Label>Cirurgias Anteriores</Label>
        <Textarea 
          placeholder="Descreva cirurgias anteriores..." 
          rows={2} 
          value={formData.cirurgiasAnteriores}
          onChange={(e) => handleInputChange('cirurgiasAnteriores', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Pressão Arterial</Label>
          <Input 
            placeholder="Ex: 120/80 mmHg" 
            value={formData.pressaoArterial}
            onChange={(e) => handleInputChange('pressaoArterial', e.target.value)}
          />
        </div>
        <div>
          <Label>Frequência Cardíaca</Label>
          <Input 
            placeholder="Ex: 72 bpm" 
            type="number"
            value={formData.frequenciaCardiaca}
            onChange={(e) => handleInputChange('frequenciaCardiaca', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Temperatura</Label>
        <Input 
          placeholder="Ex: 36.5°C" 
          type="number"
          step="0.1"
          value={formData.temperatura}
          onChange={(e) => handleInputChange('temperatura', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Peso</Label>
          <Input 
            placeholder="Ex: 70 kg" 
            type="number"
            step="0.1"
            value={formData.peso}
            onChange={(e) => handleInputChange('peso', e.target.value)}
          />
        </div>
        <div>
          <Label>Altura</Label>
          <Input 
            placeholder="Ex: 1.75 m" 
            type="number"
            step="0.01"
            value={formData.altura}
            onChange={(e) => handleInputChange('altura', e.target.value)}
          />
        </div>
        <div>
          <Label>IMC</Label>
          <Input 
            placeholder="Calculado automaticamente" 
            value={formData.imc}
            readOnly
            className="bg-muted"
          />
        </div>
      </div>

      <div>
        <Label>Estado Geral</Label>
        <Input 
          placeholder="Ex: Bom, Regular, Ruim..." 
          value={formData.estadoGeral}
          onChange={(e) => handleInputChange('estadoGeral', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        <div className="flex items-center gap-4">
          <Label className="w-64 flex-shrink-0">Faz uso de cigarros:</Label>
          <RadioGroup 
            value={formData.fumaCigarros} 
            onValueChange={(value) => handleInputChange('fumaCigarros', value)}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Sim" id="cigarros-sim" />
              <Label htmlFor="cigarros-sim" className="font-normal cursor-pointer">Sim</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Não" id="cigarros-nao" />
              <Label htmlFor="cigarros-nao" className="font-normal cursor-pointer">Não</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="flex items-center gap-4">
          <Label className="w-64 flex-shrink-0">Faz uso de álcool:</Label>
          <RadioGroup 
            value={formData.usaAlcool} 
            onValueChange={(value) => handleInputChange('usaAlcool', value)}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Sim" id="alcool-sim" />
              <Label htmlFor="alcool-sim" className="font-normal cursor-pointer">Sim</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Não" id="alcool-nao" />
              <Label htmlFor="alcool-nao" className="font-normal cursor-pointer">Não</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="flex items-center gap-4">
          <Label className="w-64 flex-shrink-0">Pratica exercícios físicos:</Label>
          <RadioGroup 
            value={formData.praticaExercicios} 
            onValueChange={(value) => handleInputChange('praticaExercicios', value)}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Sim" id="exercicios-sim" />
              <Label htmlFor="exercicios-sim" className="font-normal cursor-pointer">Sim</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Não" id="exercicios-nao" />
              <Label htmlFor="exercicios-nao" className="font-normal cursor-pointer">Não</Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      <div>
        <Label>Observações</Label>
        <Textarea 
          placeholder="Observações adicionais..." 
          rows={3} 
          value={formData.observacoes}
          onChange={(e) => handleInputChange('observacoes', e.target.value)}
        />
      </div>

      {/* Visualizador de PDF */}
      {pdfUrl && (
        <VisualizadorArquivo
          arquivoUrl={pdfUrl}
          nomeArquivo={`triagem_${protocoloId || initialData?.id || 'novo'}.pdf`}
          tipoArquivo="pdf"
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default Triagem;
